/**
 * TaskDrawer Component
 *
 * Read-only Task details. No user edits a Task (ENG-3764 D29): Tasks open and close from the records they track, so
 * the inbox only shows them and links to the work.
 */

import { Box, Button, Divider, Group, Modal, Stack, Text, Timeline, Title } from '@mantine/core';
import { IconCalendar, IconClock, IconNote, IconUser } from '@tabler/icons-react';
import type { Task } from '@medplum/fhirtypes';
import type { TaskPriority } from '../types';
import {
  TaskInboxStatusLabels,
  TaskPriorityColors,
  TaskPriorityLabels,
  TaskStatusColors,
  toInboxStatus,
} from '../types';

interface TaskDrawerProps {
  opened: boolean;
  onClose: () => void;
  task?: Task | null;
}

export function TaskDrawer({ opened, onClose, task }: TaskDrawerProps): JSX.Element {
  const status = toInboxStatus(task?.status);
  const priority = (task?.priority as TaskPriority) || 'routine';
  const dueDate = task?.restriction?.period?.end;
  const notes = (task?.note ?? []).map((note) => note.text).filter((text): text is string => Boolean(text));

  return (
    <Modal opened={opened} onClose={onClose} title={<Title order={4}>Task Details</Title>} size="md" centered>
      <Stack gap="md">
        <Box>
          <Text size="xs" c="dimmed" mb={4}>
            Description
          </Text>
          <Text size="sm">{task?.description || 'No description'}</Text>
        </Box>

        <Group grow>
          <Box>
            <Text size="xs" c="dimmed" mb={4}>
              Status
            </Text>
            <Text size="sm" c={TaskStatusColors[status]} fw={500}>
              {TaskInboxStatusLabels[status]}
            </Text>
          </Box>
          <Box>
            <Text size="xs" c="dimmed" mb={4}>
              Priority
            </Text>
            <Text size="sm" c={TaskPriorityColors[priority]} fw={500}>
              {TaskPriorityLabels[priority]}
            </Text>
          </Box>
        </Group>

        <Box>
          <Text size="xs" c="dimmed" mb={4}>
            <Group gap={4}>
              <IconCalendar size={12} />
              Due Date
            </Group>
          </Text>
          <Text size="sm">
            {dueDate
              ? new Date(dueDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              : 'No due date set'}
          </Text>
        </Box>

        <Box>
          <Text size="xs" c="dimmed" mb={4}>
            <Group gap={4}>
              <IconUser size={12} />
              Assigned To
            </Group>
          </Text>
          <Text size="sm">{task?.owner?.display || 'Unassigned'}</Text>
        </Box>

        {task?.for?.display && (
          <Box>
            <Text size="xs" c="dimmed" mb={4}>
              Related Client
            </Text>
            <Text size="sm">{task.for.display}</Text>
          </Box>
        )}

        {notes.length > 0 && (
          <Box>
            <Text size="xs" c="dimmed" mb={4}>
              <Group gap={4}>
                <IconNote size={12} />
                Notes
              </Group>
            </Text>
            {notes.map((text, index) => (
              <Text key={index} size="sm">
                {text}
              </Text>
            ))}
          </Box>
        )}

        {task && (
          <>
            <Divider my="sm" />
            <Box>
              <Text size="xs" c="dimmed" mb="sm">
                Activity
              </Text>
              <Timeline active={0} bulletSize={20} lineWidth={2}>
                <Timeline.Item bullet={<IconClock size={12} />} title="Task Created">
                  <Text size="xs" c="dimmed">
                    {task.authoredOn
                      ? new Date(task.authoredOn).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                      : 'Unknown'}
                  </Text>
                </Timeline.Item>
              </Timeline>
            </Box>
          </>
        )}

        <Divider my="sm" />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
